import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Platform, useWindowDimensions } from "react-native";

import { useOnboarding } from "@/components/owner/OnboardingProvider";
import {
  patchBusiness,
  persistOwnerOnboardingStep,
  readBusiness,
} from "@/lib/onboardingBusiness";
import { resolveOwnerOnboardingAccess } from "@/lib/onboardingResume";
import ItemsMobileLayout from "@/src/features/ownerOnboardingItems/components/ItemsMobileLayout";
import ItemsWebLayout from "@/src/features/ownerOnboardingItems/components/ItemsWebLayout";
import type { BusinessItem } from "@/src/features/ownerOnboardingItems/types";
import {
  normalizeOrder,
  sortByOrder,
} from "@/src/features/ownerOnboardingItems/utils";

type EditMode = "onboarding" | "edit-approved";

type OnboardingBusinessWithItems = {
  id: string;
  status?: string;
  isPublished?: boolean;
  isDeleted?: boolean;
  isDeactivated?: boolean;
  items?: BusinessItem[];
};

function getMode(value: unknown): EditMode {
  return value === "edit-approved" ? "edit-approved" : "onboarding";
}

function useDebouncedPatch(
  fn: (patch: Record<string, unknown>) => void,
  delay = 400
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (patch: Record<string, unknown>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      fn(patch);
    }, delay);
  };
}

export default function OnboardingItemsScreen() {
  const { businessId, setBusinessId, correctionAuthorized } = useOnboarding();

  const params = useLocalSearchParams<{
    businessId?: string;
    mode?: string;
  }>();

  const mode = getMode(params.mode);
  const isApprovedEditMode = mode === "edit-approved";

  const paramBusinessId =
    typeof params.businessId === "string" ? params.businessId : undefined;

  const effectiveBusinessId = paramBusinessId || businessId || null;

  const [items, setItems] = useState<BusinessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [itemsValidationAttempt, setItemsValidationAttempt] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);

  const businessIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!paramBusinessId) return;
    if (businessId === paramBusinessId) return;

    setBusinessId(paramBusinessId);
  }, [paramBusinessId, businessId, setBusinessId]);

  useEffect(() => {
    businessIdRef.current = effectiveBusinessId;
  }, [effectiveBusinessId]);

  const ordered = useMemo(() => sortByOrder(items), [items]);
  const showItemsError = submitted && !isApprovedEditMode && ordered.length === 0;

  /**
   * During normal onboarding, require at least one product/service.
   * During approved edit mode, allow owner to return to review/dashboard even
   * if they are temporarily cleaning up the item list.
   */
  const canGoNext = isApprovedEditMode ? true : ordered.length > 0;

  const editingItem = useMemo(() => {
    if (!editingId) return null;

    return ordered.find((item) => item.id === editingId) ?? null;
  }, [ordered, editingId]);

  useEffect(() => {
    if (ordered.length > 0 && submitted) {
      setSubmitted(false);
    }
  }, [ordered.length, submitted]);

  const doPatch = useDebouncedPatch((patch) => {
    const id = businessIdRef.current;

    if (!id) {
      console.warn("Items auto-save skipped because onboarding context is missing.");
      return;
    }

    Promise.resolve(patchBusiness(id, patch)).catch(() => {
      console.warn("Items auto-save failed.");
    });
  }, 350);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      if (!effectiveBusinessId) {
        setItems([]);
        setLoading(false);

        router.replace(
          isApprovedEditMode
            ? ("/(owner)/dashboard" as any)
            : ("/(owner)/onboarding/basics" as any)
        );

        return;
      }

      setLoading(true);

      try {
        const data = (await readBusiness(
          effectiveBusinessId
        )) as OnboardingBusinessWithItems | null;

        if (cancelled) return;

        if (!data) {
          setItems([]);
          Alert.alert(
            "Business not found",
            "We could not load this business. Please return to your dashboard."
          );
          return;
        }

        const onboardingAccess = resolveOwnerOnboardingAccess({
          status: data.status,
          isPublished: data.isPublished,
          isDeleted: data.isDeleted,
          approvedEditMode: isApprovedEditMode,
          correctionAuthorized,
        });

        if (onboardingAccess === "review") {
          router.replace({
            pathname: "/(owner)/onboarding/review",
            params: { businessId: effectiveBusinessId },
          } as any);
          return;
        }

        if (onboardingAccess === "dashboard") {
          router.replace("/(owner)/dashboard" as any);
          return;
        }

        const existingItems = Array.isArray(data.items) ? data.items : [];

        setItems(normalizeOrder(existingItems));
      } catch (error: unknown) {
        if (cancelled) return;

        console.warn("Items business load failed.");

        const message =
          error instanceof Error ? error.message : "Could not load items";

        Alert.alert("Error", message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadItems();

    return () => {
      cancelled = true;
    };
  }, [effectiveBusinessId, isApprovedEditMode, correctionAuthorized]);

  function persistWith(computeNext: (prev: BusinessItem[]) => BusinessItem[]) {
    setItems((prev) => {
      const next = normalizeOrder(computeNext(prev));

      doPatch({ items: next });

      return next;
    });
  }

  function handleAdd(item: BusinessItem) {
    if (submitError) setSubmitError(null);

    setSubmitted(false);
    persistWith((prev) => [...prev, item]);
  }

  function handleReorder(nextItems: BusinessItem[]) {
    if (submitError) setSubmitError(null);

    const normalized = normalizeOrder(nextItems);

    setItems(normalized);
    doPatch({ items: normalized });
  }

  function handleDelete(id: string) {
    if (submitError) setSubmitError(null);

    if (editingId === id) {
      setEditingId(null);
    }

    persistWith((prev) => prev.filter((item) => item.id !== id));
  }

  function handleEdit(id: string) {
    setEditingId(id);
  }

  function handleCancelEdit() {
    setEditingId(null);
  }

  function handleUpdate(updated: BusinessItem) {
    if (submitError) setSubmitError(null);

    persistWith((prev) =>
      prev.map((item) =>
        item.id === updated.id ? updated : item
      )
    );

    setEditingId(null);
  }

  function onBack() {
    const id = businessIdRef.current;

    router.push({
      pathname: "/(owner)/onboarding/brand",
      params: {
        ...(id ? { businessId: id } : {}),
        mode: isApprovedEditMode ? "edit-approved" : "onboarding",
      },
    } as any);
  }

  async function onNext() {
    const id = businessIdRef.current;

    if (!id) {
      const message = "BusinessId is missing. Go back and try again.";
      setSubmitError(message);
      Alert.alert(
        "Missing business",
        message
      );
      return;
    }

    if (!isApprovedEditMode && normalizeOrder(items).length === 0) {
      setSubmitted(true);
      setItemsValidationAttempt((previous) => previous + 1);
      setSubmitError(null);
      return;
    }

    setSaving(true);
    setSubmitError(null);

    try {
      await patchBusiness(id, {
        items: normalizeOrder(items),
      });

      if (!isApprovedEditMode) {
        await persistOwnerOnboardingStep("review");
      }
    } catch (error: any) {
      console.warn("Items save failed.");

      Alert.alert(
        "Save failed",
        error?.message || "Could not save your products/services."
      );
      setSubmitError(error?.message || "Could not save your products/services.");

      return;
    } finally {
      setSaving(false);
    }

    router.push({
      pathname: "/(owner)/onboarding/review",
      params: {
        businessId: id,
        mode: isApprovedEditMode ? "edit-approved" : "onboarding",
      },
    } as any);
  }

  const { width } = useWindowDimensions();

  const isWebDesktop = Platform.OS === "web" && width >= 1024;

  const layoutProps = {
    businessId: effectiveBusinessId,
    loading,
    saving,
    submitError,
    ordered,
    canGoNext,
    showItemsError,
    itemsValidationAttempt,
    onAdd: handleAdd,
    editingItem,
    onUpdate: handleUpdate,
    onCancelEdit: handleCancelEdit,
    onReorder: handleReorder,
    onDelete: handleDelete,
    onEdit: handleEdit,
    onBack,
    onNext,
  };

  if (isWebDesktop) {
    return <ItemsWebLayout {...layoutProps} />;
  }

  return <ItemsMobileLayout {...layoutProps} />;
}
